/**
 * Les lectures de la trésorerie, et elles seules.
 *
 * Aucun geste d'ici : le journal de trésorerie ne se lance pas parce qu'une
 * page s'ouvre ; il se lance sur accord (un passage par tranche, en SQL par
 * l'administration). Les vues sont en `security_invoker` : la RLS du noyau,
 * réservée à l'administration, s'applique à qui lit.
 *
 * La file se pagine (`range()`, `count: 'exact'`) : PostgREST plafonne une
 * réponse à 1 000 lignes sans le dire, et la production compte plus de 6 000
 * mouvements classés hors périmètre (mesure du 28/09/2026).
 */

import { db } from '../../payments/db';
import { enClair } from '../reprise/erreur';
import {
  lireLaSynthese, type EtatDeLaTresorerie, type FiltresAttente, type LigneDeSynthese,
  type MouvementEnAttente, type Sens,
} from './types';

const compta = () => db.schema('compta');

function echec(e: unknown, defaut: string): never {
  throw new Error(enClair(e, defaut));
}

const n = (v: unknown): number => (v === null || v === undefined ? 0 : Number(v));
const nn = (v: unknown): number | null => (v === null || v === undefined ? null : Number(v));
const s = (v: unknown): string | null => (v === null || v === undefined ? null : String(v));

/** 25 par page, comme les autres listes de l'espace Comptabilité. */
export const TAILLE_DE_PAGE = 25;

export async function lireLEtatDeLaTresorerie(): Promise<EtatDeLaTresorerie> {
  const { data, error } = await compta().from('v_tresorerie_synthese')
    .select('indicateur, sens, raison, nombre, montant_cents, jusqu_au');
  if (error) echec(error, 'Synthèse de la trésorerie illisible');
  const lignes: LigneDeSynthese[] = ((data ?? []) as Record<string, unknown>[]).map((l) => ({
    indicateur: String(l.indicateur),
    sens: (s(l.sens) as Sens | null),
    raison: s(l.raison),
    nombre: n(l.nombre),
    montantCents: nn(l.montant_cents),
    jusquAu: s(l.jusqu_au),
  }));
  return lireLaSynthese(lignes);
}

const COLONNES_FILE = [
  'sens', 'mouvement_id', 'ecriture_banque_id', 'journal_code', 'reference', 'value_date', 'piece_reference',
  'contrepartie', 'communication', 'montant_mouvement_cents', 'attente_cents', 'raisons', 'categorie',
  'decide_par_un_humain', 'age_jours', 'a_reclasser',
].join(', ');

export async function chargerLaFileDAttente(
  filtres: FiltresAttente,
  depuis: number,
): Promise<{ lignes: MouvementEnAttente[]; total: number }> {
  let q = compta().from('v_file_d_attente_499000').select(COLONNES_FILE, { count: 'exact' });
  if (filtres.sens) q = q.eq('sens', filtres.sens);
  if (filtres.raison) q = q.contains('raisons', [filtres.raison]);
  if (filtres.aRegarder) q = q.eq('decide_par_un_humain', false);
  /* Les plus anciens d'abord : c'est l'âge qui fait un problème. */
  const { data, error, count } = await q
    .order('value_date', { ascending: true, nullsFirst: false })
    .order('mouvement_id', { ascending: true })
    .range(depuis, depuis + TAILLE_DE_PAGE - 1);
  if (error) echec(error, 'File du compte d’attente illisible');
  const lignes = ((data ?? []) as unknown as Record<string, unknown>[]).map((l) => ({
    sens: l.sens as Sens,
    mouvementId: String(l.mouvement_id),
    ecritureId: s(l.ecriture_banque_id),
    journal: s(l.journal_code),
    reference: s(l.reference),
    dateValeur: s(l.value_date),
    piece: s(l.piece_reference),
    contrepartie: s(l.contrepartie),
    communication: s(l.communication),
    montantMouvementCents: n(l.montant_mouvement_cents),
    attenteCents: n(l.attente_cents),
    raisons: Array.isArray(l.raisons) ? (l.raisons as unknown[]).map(String) : [],
    categorie: s(l.categorie),
    decideParUnHumain: l.decide_par_un_humain === true,
    ageJours: nn(l.age_jours),
    aReclasser: l.a_reclasser === true,
  }));
  return { lignes, total: count ?? lignes.length };
}
